import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import usePageMeta from "@/hooks/use-page-meta";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Heart, Award, Users, Target, Stethoscope, Sparkles, ArrowRight } from "lucide-react";
import { useBooking } from "@/contexts/BookingContext";
import { STATS_DEFAULTS, BRAND } from "@/constants/content";

const VALUES = [
  { icon: Heart, title: "Compassion First", desc: "We treat every patient like our own family — with warmth, patience, and dignity." },
  { icon: ShieldCheck, title: "Safety & Verification", desc: "Every nurse is police-verified, medically certified, and continuously trained." },
  { icon: Award, title: "Clinical Excellence", desc: "ICU-grade protocols brought home, supervised by qualified doctors." },
  { icon: Sparkles, title: "Premium Experience", desc: "From booking to care — we obsess over the little details families remember." },
];

const TEAM_HIGHLIGHTS = [
  { title: "Clinical Leadership", desc: "Doctors & senior nurses overseeing every case." },
  { title: "Field Nurses", desc: "GNM & BSc qualified, background-verified caregivers." },
  { title: "Care Coordinators", desc: "24×7 team matching the right nurse to each patient." },
];

export default function AboutPage() {
  const { open } = useBooking();
  usePageMeta("About KinFlex Home Healthcare — Delhi NCR", "Learn about KinFlex Home Healthcare — India's premium home nursing service since 2018, serving 2,500+ families across Delhi NCR.");
  return (
    <Layout>
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-medical-grid opacity-60 pointer-events-none" />
        <div className="container-page relative">
          <div className="max-w-3xl">
            <div className="eyebrow mb-4">About KinFlex</div>
            <h1 className="font-display font-bold text-slate-900 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Home healthcare, <span className="bg-gradient-to-r from-[#0A84FF] to-[#10B981] bg-clip-text text-transparent">reimagined for families</span>.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              KinFlex Home Healthcare was founded on a simple belief — that the best place to heal is home. Since 2018, we've quietly built one of Delhi NCR's most trusted home healthcare teams, serving over 2,500 families with critical care nursing, elder care, physiotherapy, and medical procedures at home.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => open()} className="rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white px-7 h-12 font-semibold" data-testid="about-book-btn">
                Book an Appointment <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white text-slate-900 px-6 h-12 font-semibold hover:border-slate-300">
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-page grid md:grid-cols-4 gap-4 md:gap-6">
          {[
            { n: `${STATS_DEFAULTS.patients_served}+`, l: "Patients Served" },
            { n: `${STATS_DEFAULTS.professional_staff}+`, l: "Verified Nurses" },
            { n: `${STATS_DEFAULTS.years_experience}+`, l: "Years of Care" },
            { n: "24×7", l: "Support" },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl bg-[#F8FAFC] border border-slate-200/70 p-6">
              <div className="font-display font-bold text-slate-900 text-4xl">{s.n}</div>
              <div className="text-sm text-slate-600 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="section bg-[#F8FAFC]">
        <div className="container-page grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="rounded-3xl overflow-hidden ring-1 ring-slate-200 premium-shadow">
            <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=1400&auto=format&fit=crop" alt="KinFlex care team" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="eyebrow mb-3">Our Mission</div>
            <h2 className="font-display font-bold text-slate-900 text-3xl md:text-4xl tracking-tight">
              To make expert home healthcare accessible, affordable and deeply human.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              We built KinFlex because hospitals are overwhelming, and home is where healing truly happens. Our team combines clinical excellence with warmth — because families deserve both.
            </p>
            <div className="mt-8 space-y-4">
              {TEAM_HIGHLIGHTS.map((t) => (
                <div key={t.title} className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-lg bg-[#0A84FF]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Stethoscope className="h-4 w-4" style={{ color: '#0A84FF' }} />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-slate-900">{t.title}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{t.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-page">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">Our Values</div>
            <h2 className="font-display font-bold text-slate-900 text-3xl md:text-4xl lg:text-5xl tracking-tight">
              The principles behind every visit.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <div key={v.title} className="rounded-2xl border border-slate-200/70 bg-white p-6 hover:border-[#0A84FF]/30 hover:shadow-lg transition-all">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#0A84FF] to-[#10B981] text-white flex items-center justify-center">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display font-semibold text-slate-900">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[#F8FAFC]">
        <div className="container-page text-center max-w-3xl mx-auto">
          <Target className="h-10 w-10 mx-auto mb-4" style={{ color: '#0A84FF' }} />
          <h2 className="font-display font-bold text-slate-900 text-3xl md:text-4xl lg:text-5xl tracking-tight">
            Ready to bring KinFlex home?
          </h2>
          <p className="mt-4 text-slate-600 text-lg">Book an appointment and our care coordinator will call within minutes.</p>
          <Button onClick={() => open()} className="mt-8 rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white px-8 h-12 font-semibold text-base shadow-lg shadow-[#0A84FF]/25">
            Book an Appointment
          </Button>
        </div>
      </section>
    </Layout>
  );
}
